import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Ban, Check, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { INTEGRATION_ICONS } from "../../integrationProviderConfig";
import StepShell from "../StepShell";
import * as api from "../briefingService";

/**
 * Bloco J — Conexão do sistema jurídico (1º passo, precondição da geração).
 * O provedor escolhido (AdvBox/Projuris/ControlJUS) desbloqueia os
 * serviços-padrão que entram no fluxo gerado. "Não uso" gera uma triagem
 * somente de leads.
 *
 * Numa sessão embutida (embed onboarding), a conta-filha já herda o sistema
 * jurídico do parceiro — da própria ClientIntegration criada no
 * provisionamento, ou da configuração do parceiro (ver
 * juridicalProviderResolver.js no backend). Quando `briefing.juridicalProviderSource`
 * vem preenchido, o passo é TRAVADO: sem grade de opções, sem "Não uso" — só
 * o cartão do sistema herdado seguido do formulário de credencial.
 *
 * Flagship step: real provider logos on light-glass tiles, progressive
 * disclosure of credentials, and a glowing "connected" peak moment.
 */
const PROVIDERS = [
  {
    id: "advbox",
    label: "AdvBox",
    hint: "Conectar com token de API.",
    logo: INTEGRATION_ICONS.advboxIcon,
  },
  {
    id: "projuris",
    label: "Projuris",
    hint: "Usuário, senha e domínio.",
    logo: INTEGRATION_ICONS.projurisIcon,
  },
  {
    id: "controljus",
    label: "ControlJUS",
    hint: "Usuário e senha.",
    logo: INTEGRATION_ICONS.controljusIcon,
  },
  {
    id: "astrea",
    label: "Astrea",
    hint: "Usuário e senha.",
    logo: INTEGRATION_ICONS.astreaIcon,
  },
  {
    id: "faz",
    label: "SEDEP FAZ",
    hint: "Conectar com token de API.",
    logo: INTEGRATION_ICONS.fazIcon,
  },
  {
    id: "none",
    label: "Não uso sistema jurídico",
    hint: "Triagem somente de leads.",
    logo: null,
  },
];

export default function BlocoJJuridico({ clientId, briefing, autosave }) {
  const reduceMotion = useReducedMotion();
  const locked = !!briefing.juridicalProviderSource;
  const [provider, setProvider] = useState(briefing.juridicalProvider || null);
  const [token, setToken] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [domain, setDomain] = useState("");
  const [status, setStatus] = useState(
    briefing.juridicalIntegrationId || briefing.juridicalProvider === "none"
      ? "connected"
      : "idle",
  ); // idle | connecting | connected | error
  const [errorMsg, setErrorMsg] = useState("");

  function selectProvider(id) {
    setProvider(id);
    setStatus("idle");
    setErrorMsg("");
    if (id === "none") {
      autosave({ juridicalProvider: "none", juridicalIntegrationId: null });
      setStatus("connected");
    }
  }

  async function handleConnect() {
    setStatus("connecting");
    setErrorMsg("");
    try {
      let credentials;
      if (provider === "advbox") {
        await api.validateAdvboxToken(token);
        credentials = { token };
      } else if (provider === "faz") {
        await api.validateFazToken(token);
        credentials = { token };
      } else if (provider === "controljus") {
        await api.validateControljusCredentials({ username, password });
        credentials = { username, password };
      } else if (provider === "astrea") {
        await api.validateAstreaCredentials({ username, password });
        credentials = { username, password };
      } else {
        await api.validateProjurisCredentials({ username, password, domain });
        credentials = { username, password, domain };
      }
      const integration = await api.connectJuridical(clientId, provider, credentials);
      autosave({
        juridicalProvider: provider,
        juridicalIntegrationId: integration?.id || integration?.integration?.id || null,
      });
      setStatus("connected");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Não foi possível conectar. Verifique as credenciais.");
    }
  }

  const canConnect =
    provider === "advbox" || provider === "faz"
      ? token.trim().length > 0
      : provider === "controljus" || provider === "astrea"
        ? username.trim() && password.trim()
        : provider === "projuris"
          ? username.trim() && password.trim() && domain.trim()
          : false;

  const selectedMeta = PROVIDERS.find((p) => p.id === provider);
  // Ficar de fora daqui e silencioso: o passo herdado renderiza o cartao e NENHUM
  // formulario, e a conta trava sem como conectar.
  const isCredentialProvider = PROVIDERS.some(
    (p) => p.id === provider && p.id !== "none",
  );
  const isConnected = status === "connected" && isCredentialProvider;

  const reveal = {
    initial: { opacity: 0, y: reduceMotion ? 0 : 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: reduceMotion ? 0 : -8 },
  };

  const title = locked
    ? `Conecte sua conta do ${selectedMeta?.label || "sistema jurídico"}`
    : "Conecte seu sistema jurídico";
  const description = locked
    ? `Seu acesso já está vinculado ao ${selectedMeta?.label || "sistema jurídico"}. Informe as credenciais para liberar os serviços de atendimento.`
    : "É o primeiro passo: ele libera os serviços de atendimento (consulta de processo, audiências, financeiro) que vamos montar no seu fluxo. Você só conecta aqui — a importação dos dados vem depois.";

  return (
    <StepShell title={title} description={description}>
      {locked ? (
        <InheritedProviderCard
          meta={selectedMeta}
          partnerName={briefing.inheritedJuridical?.partnerName}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-4">
          {PROVIDERS.map((p) => {
            const active = provider === p.id;
            return (
              <motion.button
                key={p.id}
                type="button"
                onClick={() => selectProvider(p.id)}
                whileHover={reduceMotion ? undefined : { y: -2 }}
                whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 24 }}
                className={cn(
                  "group relative flex flex-col items-start gap-3 rounded-2xl border p-4 text-left transition-colors",
                  active
                    ? "border-[rgb(var(--cyan)/0.7)] bg-[rgb(var(--cyan)/0.06)] ring-2 ring-[rgb(var(--cyan)/0.25)]"
                    : "border-border bg-surface/40 hover:border-border-strong",
                )}
              >
                {active && (
                  <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-500 text-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                )}
                <span className="tw-glass-light flex h-12 w-12 items-center justify-center rounded-xl">
                  {p.logo ? (
                    <img src={p.logo} alt={`Logo ${p.label}`} className="h-7 w-7 object-contain" />
                  ) : (
                    <Ban className="h-6 w-6 text-neutral-500" />
                  )}
                </span>
                <span className="space-y-0.5">
                  <span className="block font-medium text-foreground">{p.label}</span>
                  <span className="block text-xs text-muted-foreground">{p.hint}</span>
                </span>
              </motion.button>
            );
          })}
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* Connected peak state */}
        {isConnected && (
          <motion.div
            key="connected"
            variants={reveal}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 flex items-center gap-4 rounded-2xl border border-[rgb(var(--cyan)/0.3)] bg-[rgb(var(--cyan)/0.06)] p-4"
          >
            <span
              className={cn(
                "tw-glass-light flex h-12 w-12 items-center justify-center rounded-xl",
                !reduceMotion && "animate-pulse-glow-cyan",
              )}
            >
              {selectedMeta?.logo && (
                <img src={selectedMeta.logo} alt="" className="h-7 w-7 object-contain" />
              )}
            </span>
            <div className="flex-1">
              <p className="flex items-center gap-1.5 font-medium text-foreground">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--cyan))]" />
                Conectado ao {selectedMeta?.label}
              </p>
              <p className="text-xs text-muted-foreground">
                Os serviços jurídicos já estão disponíveis para o seu fluxo.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStatus("idle")}
            >
              Reconectar
            </Button>
          </motion.div>
        )}

        {/* AdvBox credentials */}
        {provider === "advbox" && !isConnected && (
          <motion.div
            key="advbox-form"
            variants={reveal}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 space-y-2"
          >
            <Label htmlFor="advbox-token">Token de API do AdvBox</Label>
            <Input
              id="advbox-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Cole aqui o token gerado no AdvBox"
              autoComplete="off"
            />
            <ConnectRow
              onConnect={handleConnect}
              canConnect={canConnect}
              status={status}
              errorMsg={errorMsg}
            />
          </motion.div>
        )}

        {/* Projuris credentials */}
        {provider === "projuris" && !isConnected && (
          <motion.div
            key="projuris-form"
            variants={reveal}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="pj-user">Usuário</Label>
                <Input id="pj-user" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pj-pass">Senha</Label>
                <Input id="pj-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pj-domain">Domínio</Label>
                <Input id="pj-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="ex.: seuescritorio" autoComplete="off" />
              </div>
            </div>
            <ConnectRow
              onConnect={handleConnect}
              canConnect={canConnect}
              status={status}
              errorMsg={errorMsg}
            />
          </motion.div>
        )}

        {/* ControlJUS credentials */}
        {provider === "controljus" && !isConnected && (
          <motion.div
            key="controljus-form"
            variants={reveal}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cj-user">Usuário</Label>
                <Input id="cj-user" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cj-pass">Senha</Label>
                <Input id="cj-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
              </div>
            </div>
            <ConnectRow
              onConnect={handleConnect}
              canConnect={canConnect}
              status={status}
              errorMsg={errorMsg}
            />
          </motion.div>
        )}

        {provider === "astrea" && !isConnected && (
          <motion.div
            key="astrea-form"
            variants={reveal}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="astrea-user">Usuário</Label>
                <Input
                  id="astrea-user"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                  placeholder="E-mail usado em app.astrea.net.br"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="astrea-pass">Senha</Label>
                <Input
                  id="astrea-pass"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
            <ConnectRow
              onConnect={handleConnect}
              canConnect={canConnect}
              status={status}
              errorMsg={errorMsg}
            />
          </motion.div>
        )}

        {provider === "faz" && !isConnected && (
          <motion.div
            key="faz-form"
            variants={reveal}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="faz-token">Token de API do SEDEP FAZ</Label>
              <Input
                id="faz-token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoComplete="off"
                placeholder="Cole aqui o token"
              />
            </div>
            <ConnectRow
              onConnect={handleConnect}
              canConnect={canConnect}
              status={status}
              errorMsg={errorMsg}
            />
          </motion.div>
        )}

        {/* No juridical system */}
        {provider === "none" && (
          <motion.p
            key="none-note"
            variants={reveal}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 rounded-xl border border-border bg-surface-inset/50 p-4 text-sm text-muted-foreground"
          >
            Sem sistema jurídico conectado, a IA fará apenas a triagem e o
            encaminhamento de novos leads. Você pode conectar mais tarde.
          </motion.p>
        )}
      </AnimatePresence>
    </StepShell>
  );
}

/** Cartão estático (não clicável) do sistema herdado — substitui a grade de opções. */
function InheritedProviderCard({ meta, partnerName }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-[rgb(var(--cyan)/0.7)] bg-[rgb(var(--cyan)/0.06)] p-4 ring-2 ring-[rgb(var(--cyan)/0.25)]">
      <span className="tw-glass-light flex h-12 w-12 items-center justify-center rounded-xl">
        {meta?.logo ? (
          <img src={meta.logo} alt={`Logo ${meta.label}`} className="h-7 w-7 object-contain" />
        ) : (
          <Ban className="h-6 w-6 text-neutral-500" />
        )}
      </span>
      <div className="space-y-0.5">
        <p className="font-medium text-foreground">{meta?.label || "Sistema jurídico"}</p>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link2 className="h-3.5 w-3.5" />
          {partnerName ? `Vinculado pela ${partnerName}` : "Vinculado ao seu acesso"}
        </p>
      </div>
    </div>
  );
}

function ConnectRow({ onConnect, canConnect, status, errorMsg }) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <Button onClick={onConnect} disabled={!canConnect || status === "connecting"}>
        {status === "connecting" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Validar e conectar
      </Button>
      {status === "error" && (
        <span className="flex items-center gap-1.5 text-sm text-destructive">
          <XCircle className="h-4 w-4" /> {errorMsg}
        </span>
      )}
    </div>
  );
}
